import { MenuButton, MenuItemWithIcon } from "@msys/ui";
import { BugReport as BugReportIcon } from "@mui/icons-material";
import { Divider } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useNavigate } from "react-router-dom";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { DeleteTemplateMenuItem } from "../buttons/DeleteTemplateMenuItem.js";
import { DuplicateTemplateMenuItem } from "../buttons/DuplicateTemplateMenuItem.js";
import { ExportTemplatesMenuItem } from "../buttons/ExportTemplatesMenuItem.js";
import { TemplateBulkUpdateProductsButton } from "../buttons/TemplateBulkUpdateProductsButton.js";
import { TemplateRecomputeExpressionDependencyGraphButton } from "../buttons/TemplateRecomputeExpressionDependencyGraphButton.js";
import { QuoteTemplateManageVersionsMenuItem } from "./buttons/QuoteTemplateManageVersionsMenuItem.js";
import { ToggleQuoteTemplateBindingMenuItem } from "./buttons/ToggleQuoteTemplateBindingMenuItem.js";
import { TemplateQuoteTemplateLatestMenu_QuoteTemplateFragment } from "./TemplatesQuoteTemplateLatestMenu.generated.js";

interface Props {
  doc: TemplateQuoteTemplateLatestMenu_QuoteTemplateFragment;
  pathToDoc: string;
  pathToDocList: string;
  pathToDocVersion: string;
  onDuplicateRefetchQueries?: string[];
  onUpdateDataRefetchQueries?: string[];
}

export const TemplatesQuoteTemplateLatestMenu = ({
  doc,
  pathToDoc,
  pathToDocList,
  pathToDocVersion,
  onDuplicateRefetchQueries,
  onUpdateDataRefetchQueries,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslate(["Quote", "Global"]);
  return (
    <MenuButton>
      <RestrictedByOrganisationPermissionWithDebug
        permission="MANAGE_TEMPLATE_SETTINGS"
        otherwise={
          <ToggleQuoteTemplateBindingMenuItem
            doc={doc}
            docId={doc.id}
            disabled={true}
          />
        }
      >
        <ToggleQuoteTemplateBindingMenuItem doc={doc} docId={doc.id} />
      </RestrictedByOrganisationPermissionWithDebug>
      <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
        <Divider />
        <ExportTemplatesMenuItem
          docIds={[
            {
              docId: doc.id,
              versionNumber: doc.resolvedAsReadModelVersionNumber ?? null,
            },
          ]}
          title={t("Export template", { ns: "Quote" })}
        />
      </RestrictedByOrganisationPermissionWithDebug>
      <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
        <DuplicateTemplateMenuItem
          doc={doc}
          pathToDocList={pathToDocList}
          onDuplicateRefetchQueries={onDuplicateRefetchQueries}
        />
      </RestrictedByOrganisationPermissionWithDebug>
      <RestrictedByCapabilityWithDebug capability="PIM">
        <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
          <TemplateBulkUpdateProductsButton docId={doc.id} />
        </RestrictedByOrganisationPermissionWithDebug>
      </RestrictedByCapabilityWithDebug>
      <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
        <QuoteTemplateManageVersionsMenuItem
          docId={doc.id}
          pathToDoc={pathToDoc}
          refetchQueries={onUpdateDataRefetchQueries}
        />
      </RestrictedByOrganisationPermissionWithDebug>
      <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
        <Divider />
        <TemplateRecomputeExpressionDependencyGraphButton docId={doc.id} />
        <MenuItemWithIcon
          icon={<BugReportIcon />}
          onClick={() => navigate(`${pathToDocVersion}/troubleshoot`)}
        >
          {t("Troubleshoot", { ns: "Global" })}
        </MenuItemWithIcon>
      </RestrictedByOrganisationPermissionWithDebug>
      <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
        <Divider />
        <DeleteTemplateMenuItem doc={doc} pathToDocList={pathToDocList} />
      </RestrictedByOrganisationPermissionWithDebug>
    </MenuButton>
  );
};
